<script lang="ts">
  import type { StorePinData } from './storeTypes';
  import { debugLog } from './debugUtils';
  import { highlightShape, unhighlightShape } from './shapeUtils';
  import { highlightStorePin, resetStorePin } from './pinUtils';
  
  // Props
  export let svgElement: SVGSVGElement | null = null;
  export let highlightedStoreId: string | null = null;
  export let currentFloorId: number | null = null;
  export let cachedPinsByFloor: Map<number, StorePinData[]>;
  
  // New configuration props
  export let shapeHighlighting: any = {};
  export let storePins: any = {};
  
  // Local state
  let previousHighlightedId: string | null = null;
  
  // When highlighted store changes, update highlighting
  $: if (svgElement && (highlightedStoreId !== previousHighlightedId)) {
    updateHighlightedStore();
    previousHighlightedId = highlightedStoreId;
  }
  
  // Update highlighting for pins and shapes
  function updateHighlightedStore() {
    if (!svgElement) {
      debugLog('Cannot update highlighting: SVG element not found', 'floorChanges');
      return;
    }
    
    debugLog(`Updating store highlighting: ${highlightedStoreId || 'none'}`, 'floorChanges');
    
    // Clear all highlighted shapes first
    const highlightedShapes = svgElement.querySelectorAll('.highlighted-shape');
    highlightedShapes.forEach(shape => {
      const shapeId = shape.id;
      if (shapeId) {
        unhighlightShape(svgElement!, shapeId);
      }
    });
    
    // Also remove all highlight overlays
    const overlays = svgElement.querySelectorAll('.shape-highlight-overlay');
    overlays.forEach(overlay => {
      overlay.parentNode?.removeChild(overlay);
    });
    
    // Find all pin containers
    const pinContainers = svgElement.querySelectorAll('.pin-container');
    
    // If we have a store to highlight, attempt to find and highlight its shape first
    if (highlightedStoreId && currentFloorId !== null) {
      // Find the pin data for the highlighted store
      const highlightedPin = cachedPinsByFloor.get(currentFloorId)?.find(p => p.id === highlightedStoreId);
      
      if (highlightedPin) {
        debugLog(`Found pin data for highlighted store ${highlightedStoreId}`, 'shapeMatching');
        
        // Highlight the shape for this store if available
        if (highlightedPin.shapeId) {
          debugLog(`Highlighting shape ${highlightedPin.shapeId} for store ${highlightedStoreId}`, 'shapeMatching');
          
          // Use the specialized shape highlight options
          highlightShape(
            svgElement, 
            highlightedPin.shapeId, 
            highlightedPin,
            shapeHighlighting.fillColor,
            shapeHighlighting.strokeColor,
            shapeHighlighting.strokeWidth,
            shapeHighlighting.opacity
          );
        } else if (highlightedPin.relatedShapeIds && highlightedPin.relatedShapeIds.length > 0) {
          debugLog(`Highlighting ${highlightedPin.relatedShapeIds.length} related shapes for store ${highlightedStoreId}`, 'shapeMatching');
          
          // Highlight all related shapes
          highlightedPin.relatedShapeIds.forEach(shapeId => {
            highlightShape(
              svgElement!, 
              shapeId, 
              highlightedPin,
              shapeHighlighting.fillColor,
              shapeHighlighting.strokeColor,
              shapeHighlighting.strokeWidth,
              shapeHighlighting.opacity
            );
          });
        }
      } else {
        // Check all floors in case the store is on a different floor
        debugLog(`Store ${highlightedStoreId} not found on current floor, checking all floors`, 'shapeMatching');
        
        for (const [floorId, pins] of cachedPinsByFloor.entries()) {
          const pin = pins.find(p => p.id === highlightedStoreId);
          if (pin && pin.shapeId) {
            debugLog(`Found store ${highlightedStoreId} on floor ${floorId}, but we're on floor ${currentFloorId}`, 'shapeMatching');
            break;
          }
        }
      }
    }
    
    // Now update each pin container
    pinContainers.forEach(container => {
      const containerId = container.getAttribute('data-store-id');
      if (!containerId) return;
      
      if (containerId === highlightedStoreId) {
        // Highlight this pin
        debugLog(`Highlighting pin for store ${containerId}`, 'floorChanges');
        
        highlightStorePin(
          svgElement!, 
          container, 
          containerId, 
          storePins
        );
      } else {
        // Reset to default
        resetStorePin(svgElement!, container, storePins);
        
        // Find the corresponding pin data to get all related shapes
        let pinData: StorePinData | undefined;
        if (currentFloorId !== null && cachedPinsByFloor.has(currentFloorId)) {
          pinData = cachedPinsByFloor.get(currentFloorId)?.find(p => p.id === containerId);
        }
        
        // Unhighlight the corresponding shape if available
        const shapeId = container.getAttribute('data-shape-id');
        if (shapeId) {
          unhighlightShape(svgElement!, shapeId, pinData);
        }
      }
    });
    
    // Log if we couldn't find any pins
    if (highlightedStoreId && pinContainers.length === 0) {
      debugLog('No pins found to highlight, but highlighting was requested', 'floorChanges');
    }
  }
  
  // Expose this function to the parent component
  export function highlightShapeById(storeId: string) {
    highlightedStoreId = storeId;
    updateHighlightedStore();
  }
</script> 